// features/store.ts
import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { persistReducer, persistStore } from "redux-persist";
import storage from "./storage";

import ListingReducer from "../features/listing_slice";
import GeneralReducer from "../features/generalSlice";
import AuthReducer from "../features/authSlice";
import RecipeReducer from "../features/recipeSlice";

const rootReducer = combineReducers({
  listing: ListingReducer,
  general: GeneralReducer,
  culineerAuth: AuthReducer,
  recipe: RecipeReducer,
});

const persistConfig = {
  key: "root",
  storage,
  blacklist: ["general"],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

// ⚡ merge persisted + preloaded state
function mergeStates(persisted: any, preloaded: any) {
  return {
    ...persisted,
    ...preloaded,
    // deep merge specific reducers if needed
    listing: {
      ...persisted?.listing,
      ...preloaded?.listing,
    },
    culineerAuth: {
      ...persisted?.culineerAuth,
      ...preloaded?.culineerAuth,
    },
  };
}

// Factory per request (SSR safe)
export function makeStore(preloadedState = {}) {
  return configureStore({
    reducer: persistedReducer,
    preloadedState,
    devTools: process.env.NODE_ENV !== "production",
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({ serializableCheck: false }),
  });
}

// Singleton for client
// features/store.ts
let store: ReturnType<typeof makeStore>;
let hasHydrated = false;

export function getStore(preloadedState = {}) {
  if (!store) {
    store = makeStore(preloadedState);
  } else if (!hasHydrated && Object.keys(preloadedState).length > 0) {
    const merged = mergeStates(store.getState(), preloadedState);
    store = makeStore(merged);
    hasHydrated = true;
  }
  return store;
}

export const persistor = persistStore(getStore());

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = ReturnType<typeof getStore>["dispatch"];
